import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { Theme, themes } from '../lib/themes';

type ThemeContextType = {
  currentTheme: Theme;
  setTheme: (themeId: string) => Promise<void>;
  themes: Theme[];
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const DEFAULT_THEME = themes.find(t => t.id === 'squeaky-clean')!;

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [currentTheme, setCurrentTheme] = useState<Theme>(DEFAULT_THEME);

  useEffect(() => {
    if (user) {
      loadUserTheme();
    } else {
      setCurrentTheme(DEFAULT_THEME);
    }
  }, [user]);

  const loadUserTheme = async () => {
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('theme_id')
        .eq('id', user!.id)
        .single();

      if (profile?.theme_id) {
        const theme = themes.find(t => t.id === profile.theme_id);
        if (theme) {
          setCurrentTheme(theme);
          applyTheme(theme);
        }
      }
    } catch (error) {
      console.error('Error loading user theme:', error);
    }
  };

  const applyTheme = (theme: Theme) => {
    document.documentElement.style.setProperty('--color-background', theme.colors.background);
    document.documentElement.style.setProperty('--color-accent1', theme.colors.accent1);
    document.documentElement.style.setProperty('--color-accent2', theme.colors.accent2);
    document.documentElement.style.setProperty('--color-accent3', theme.colors.accent3);
    document.documentElement.style.setProperty('--color-font', theme.colors.font);
    document.documentElement.style.setProperty('--color-subheaders', theme.colors.subheaders);
    document.documentElement.style.setProperty('--color-hovers', theme.colors.hovers);
    document.documentElement.style.setProperty('--color-input-text', theme.colors.inputText);
    
    document.body.style.backgroundColor = theme.colors.background;
    document.body.style.color = theme.colors.font;
  };

  const setTheme = async (themeId: string) => {
    const theme = themes.find(t => t.id === themeId);
    if (!theme || !user) return;

    try {
      await supabase
        .from('profiles')
        .update({ theme_id: themeId })
        .eq('id', user.id);

      setCurrentTheme(theme);
      applyTheme(theme);
    } catch (error) {
      console.error('Error saving theme preference:', error);
    }
  };

  useEffect(() => {
    applyTheme(currentTheme);
  }, []);

  return (
    <ThemeContext.Provider value={{ currentTheme, setTheme, themes }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}